<script lang="ts">
	import { base } from '$app/paths';
	import * as api from '$lib/shared/apis';
	import Modal from './Modal.svelte';
	import ObjectMeta from './ObjectMeta.svelte';
	import { sortSubmissions } from './shared/submissions';
	import { user } from './stores/auth';

	export let object: ObjectType;
	export let showButtons = false;
	let error = '';

	let releaseConfirmationIsVisible = false;
	let submitConfirmationIsVisible = false;

	const getLatestSubmission = async () => {
		const { response, json } = await api.post({
			query: /* GraphQL */ `
				query ($objectId: Int!) {
					submissions(objectId: $objectId) {
						id
						transcriptions {
							... on LetterTranscription {
								transcription
							}
						}
					}
				}
			`,
			variables: {
				objectId: object.id,
			},
		});
		if (response.ok && json.data) {
			object.submissions = json.data.submissions;
			if (object.submissions.length) {
				object.submissions = sortSubmissions(object.submissions);
			}
		}
	};

	const afterRelease = () => {
		submitConfirmationIsVisible = false;
		releaseConfirmationIsVisible = false;
		object.lockedBy = null;
		object.lockedUntil = null;
		$user.lockedObjects = $user.lockedObjects.filter((o) => o.id !== object.id);
	};

	const unlock = async () => {
		const { response, json } = await api.post({
			query: /* GraphQL */ `
				mutation unlockObject($objectId: Int!) {
					unlockObject(objectId: $objectId)
				}
			`,
			variables: {
				objectId: object.id,
			},
		});
		if (response.ok) {
			object.status = json.data.unlockObject;
			$user.lockedObjects = $user.lockedObjects.filter((o) => o.id !== object.id);
		}
		afterRelease();
	};

	const submit = async (type: SubmissionType = 'INCOMPLETE') => {
		if (!submitConfirmationIsVisible) {
			submitConfirmationIsVisible = true;
			return;
		}

		await getLatestSubmission();

		if (!object.submissions || !object.submissions.length) {
			unlock();
			return;
		}

		error = '';

		const { response, json } = await api.post({
			query: /* GraphQL */ `
				mutation (
					$submissionId: String!
					$transcriptions: [TranscriptionInput!]!
					$type: SubmissionType!
				) {
					updateSubmission(
						submissionId: $submissionId
						transcriptions: $transcriptions
						type: $type
					) {
						status
						submission {
							id
							createdAt
							user {
								id
								name
							}
							transcriptions {
								... on LetterTranscription {
									transcription
								}
							}
						}
					}
				}
			`,
			variables: {
				submissionId: object.submissions[0].id,
				transcriptions: object.submissions[0].transcriptions,
				type,
			},
		});

		if (response.ok) {
			if (json.data) {
				object.status = json.data.updateSubmission.status;
				afterRelease();
			} else {
				if (json.errors) {
					error = json.errors.map((error) => error.message).join(', ');
				}
			}
		}
	};
</script>

<div class="relative">
	<a href="{base}/object/{object.id}/" class="block no-underline pt-2 pb-4">
		{#if object.incipit}
			<div class="py-4">
				<ObjectMeta {object} />
			</div>

			<div class="text-lg-fluid text-ellipsis whitespace-nowrap overflow-hidden pb-2">
				{object.incipit}…
			</div>
		{:else}
			<div class="py-4">
				<ObjectMeta
					{object}
					attributes={['date', 'address', 'pageCount', 'task', 'latestApprovalCount']}
				/>
			</div>

			<dl
				class="flex text-lg-fluid text-ellipsis pb-2 whitespace-nowrap normal-case space-x-8"
			>
				{#if object.author}
					<div>
						<dt>Von</dt>
						<dd>{object.author}</dd>
					</div>
				{/if}
				{#if object.recipient}
					<div>
						<dt>An</dt>
						<dd>{object.recipient}</dd>
					</div>
				{/if}
			</dl>
		{/if}
	</a>
	{#if showButtons}
		<div class="absolute top-6 right-0 flex space-x-5 items-center pointer-events-none">
			<span class="uppercase underline text-sm" aria-hidden="true">Bearbeiten</span>
			<button
				type="button"
				id="release-btn"
				class="z-10 relative uppercase underline text-sm pointer-events-auto focus-visible:ring-2 ring-blue"
				on:click|capture|stopPropagation={() => {
					releaseConfirmationIsVisible = true;
				}}>Freigeben</button
			>
		</div>
	{/if}
</div>

{#if releaseConfirmationIsVisible}
	<Modal
		on:close={() => {
			releaseConfirmationIsVisible = false;
		}}
	>
		<h2 class="text-lg-fluid">Wollen Sie Ihre Version wirklich freigeben?</h2>
		<p>Der Brief wird wieder freigegeben und andere Personen können daran weiterarbeiten.</p>

		{#if error}
			<p class="text-sm-fluid text-orange mt-3" aria-live="assertive">{error}</p>
		{/if}

		<div slot="bottom" class="flex space-x-5">
			<button
				type="button"
				id="save-btn"
				class="button"
				on:click={() => {
					unlock();
				}}>Ja, freigeben</button
			>
			<button
				type="button"
				id="delete-button"
				class="button"
				on:click={() => {
					releaseConfirmationIsVisible = false;
				}}>Abbrechen</button
			>
		</div>
	</Modal>
{/if}
